import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, ExternalLink, RefreshCw, Tag, X } from 'lucide-react'
import { apiFetch } from '../lib/api'
import type { ActivityRow, Paged } from '../lib/types'
import { PageHeader, Field, formatDate } from '../components/page'
import { EmptyState, ErrorState, Spinner } from '../components/states'
import { Badge, StatusBadge } from '../components/badge'
import { ConfirmDialog } from '../components/confirm-dialog'

interface Detail {
	instance: Record<string, any>
	user: Record<string, any> | null
	legacySubscription: Record<string, any> | null
	chargebee: { subscription: any; customer: any } | null
	chargebeeError: string | null
	chargebeeUrl: string | null
	chargebeeCustomerUrl: string | null
}

// Chargebee stores money in minor units.
function money(amount?: number | null, currency?: string | null): string {
	if (amount === null || amount === undefined) return '—'
	return `${(amount / 100).toFixed(2)} ${currency ?? ''}`.trim()
}

function unix(seconds?: number | null): string {
	return seconds ? formatDate(new Date(seconds * 1000).toISOString()) : '—'
}

export function SubscriptionDetailPage() {
	const { id = '' } = useParams()
	const queryClient = useQueryClient()
	const [dialog, setDialog] = useState<'cancel' | 'reactivate' | null>(null)
	const [endOfTerm, setEndOfTerm] = useState(true)
	const [reason, setReason] = useState('')
	const [couponCode, setCouponCode] = useState('')
	const [notice, setNotice] = useState<string | null>(null)

	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['subscription', id],
		queryFn: () => apiFetch<Detail>(`/api/admin/subscriptions/${id}`),
	})

	const timeline = useQuery({
		queryKey: ['timeline', 'subscription', data?.instance?.chargebeeSubscriptionId],
		enabled: Boolean(data?.instance?.chargebeeSubscriptionId),
		queryFn: () =>
			apiFetch<Paged<ActivityRow>>(
				`/api/admin/logs/timeline/subscription/${data!.instance.chargebeeSubscriptionId}?limit=15`,
			),
	})

	function afterChange(message: string) {
		setNotice(message)
		setDialog(null)
		queryClient.invalidateQueries({ queryKey: ['subscription', id] })
		queryClient.invalidateQueries({ queryKey: ['timeline'] })
	}

	const resync = useMutation({
		mutationFn: () =>
			apiFetch<{ changed: boolean; before: any; after: any }>(`/api/admin/subscriptions/${id}/resync`, { method: 'POST' }),
		// The response says whether anything moved, so an operator is not left
		// wondering whether the button did anything.
		onSuccess: (r) =>
			afterChange(r.changed ? `Resynced: status ${r.before.status} → ${r.after.status}` : 'Already up to date.'),
	})

	const cancel = useMutation({
		mutationFn: () =>
			apiFetch(`/api/admin/subscriptions/${id}/cancel`, { method: 'POST', body: { endOfTerm, reason: reason || undefined } }),
		onSuccess: () =>
			afterChange(
				endOfTerm
					? 'Cancelled at end of term. Local state follows once Chargebee sends the webhook.'
					: 'Cancelled immediately. Local state follows once Chargebee sends the webhook.',
			),
	})

	const reactivate = useMutation({
		mutationFn: () => apiFetch(`/api/admin/subscriptions/${id}/reactivate`, { method: 'POST' }),
		onSuccess: () => afterChange('Reactivated in Chargebee.'),
	})

	const applyCoupon = useMutation({
		mutationFn: () => apiFetch(`/api/admin/subscriptions/${id}/coupons`, { method: 'POST', body: { couponCode } }),
		onSuccess: () => {
			setCouponCode('')
			afterChange('Coupon applied.')
		},
	})

	const removeCoupon = useMutation({
		mutationFn: (code: string) =>
			apiFetch(`/api/admin/subscriptions/${id}/coupons/${encodeURIComponent(code)}`, { method: 'DELETE' }),
		onSuccess: () => afterChange('Coupon removed.'),
	})

	if (isPending) return <Spinner />
	if (error) return <ErrorState error={error} onRetry={refetch} />

	const { instance, user, legacySubscription, chargebee, chargebeeError, chargebeeUrl, chargebeeCustomerUrl } = data
	const sub = chargebee?.subscription
	const billable = Boolean(instance.chargebeeSubscriptionId)
	const mutationError = [resync.error, cancel.error, reactivate.error, applyCoupon.error, removeCoupon.error].find(Boolean)

	return (
		<>
			<Link to="/subscriptions" className="mb-4 inline-flex items-center gap-1.5 text-sm text-ink-soft hover:underline">
				<ArrowLeft className="h-4 w-4" /> Subscriptions
			</Link>

			<PageHeader
				title={`${instance.planSlug} · ${instance.billingTerm ?? '—'}`}
				subtitle={user?.email ?? 'No linked account'}
				actions={
					<>
						{chargebeeUrl && (
							<a href={chargebeeUrl} target="_blank" rel="noreferrer" className="btn-secondary">
								<ExternalLink className="h-4 w-4" /> Chargebee
							</a>
						)}
						{billable && (
							<button type="button" className="btn-secondary" onClick={() => resync.mutate()} disabled={resync.isPending}>
								<RefreshCw className={`h-4 w-4 ${resync.isPending ? 'animate-spin' : ''}`} />
								Resync
							</button>
						)}
						{billable && sub?.status !== 'cancelled' && (
							<button type="button" className="btn-danger" onClick={() => setDialog('cancel')}>
								<X className="h-4 w-4" /> Cancel
							</button>
						)}
						{billable && sub?.status === 'cancelled' && (
							<button type="button" className="btn-primary" onClick={() => setDialog('reactivate')}>
								Reactivate
							</button>
						)}
					</>
				}
			/>

			{notice && <p className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</p>}
			{mutationError && (
				<p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{(mutationError as Error).message}</p>
			)}
			{chargebeeError && (
				<p className="mb-4 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
					<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
					{chargebeeError} — the local details below are still accurate.
				</p>
			)}

			<div className="grid gap-4 lg:grid-cols-3">
				<section className="card p-4 lg:col-span-2">
					<h2 className="mb-3 text-sm font-semibold">Instance (local)</h2>
					<dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
						<Field label="Status"><StatusBadge value={instance.status} /></Field>
						<Field label="hosting.de id"><span className="mono">{instance.hostingdeId}</span></Field>
						<Field label="Source">{instance.source}</Field>
						<Field label="Chargebee subscription"><span className="mono">{instance.chargebeeSubscriptionId}</span></Field>
						<Field label="Deletion scheduled">{formatDate(instance.deletionScheduledAt)}</Field>
						<Field label="Created">{formatDate(instance.createdAt)}</Field>
					</dl>
					{instance.error && (
						<p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{instance.error}</p>
					)}
				</section>

				<section className="card p-4">
					<h2 className="mb-3 text-sm font-semibold">Billing (live)</h2>
					{sub ? (
						<dl className="grid gap-3">
							<Field label="Status"><StatusBadge value={sub.status} /></Field>
							<Field label="Amount">{money(sub.mrr ?? sub.subscription_items?.[0]?.amount, sub.currency_code)}</Field>
							<Field label="Current term ends">{unix(sub.current_term_end)}</Field>
							<Field label="Next billing">{unix(sub.next_billing_at)}</Field>
							<Field label="Due invoices">{sub.due_invoices_count ?? 0}</Field>
							{chargebeeCustomerUrl && (
								<Field label="Customer">
									<a href={chargebeeCustomerUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">
										{chargebee?.customer?.email ?? 'open in Chargebee'}
									</a>
								</Field>
							)}
						</dl>
					) : legacySubscription ? (
						<div className="grid gap-3">
							<Badge tone="warning">WHMCS — not billed through Chargebee</Badge>
							<Field label="Product">{legacySubscription.productName}</Field>
							<Field label="Status"><StatusBadge value={legacySubscription.status} /></Field>
							<Field label="Next due">{formatDate(legacySubscription.nextDueDate)}</Field>
						</div>
					) : (
						<p className="text-sm text-ink-faint">Not billed through Chargebee.</p>
					)}
				</section>

				{sub && (
					<section className="card p-4 lg:col-span-3">
						<h2 className="mb-3 text-sm font-semibold">Coupons</h2>
						{sub.coupons?.length ? (
							<ul className="mb-4 flex flex-wrap gap-2">
								{sub.coupons.map((c: any) => (
									<li key={c.coupon_id} className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-1.5 text-sm">
										<Tag className="h-3.5 w-3.5 text-ink-faint" />
										<span className="mono">{c.coupon_id}</span>
										<button
											type="button"
											aria-label={`Remove ${c.coupon_id}`}
											className="text-ink-faint hover:text-red-600"
											onClick={() => removeCoupon.mutate(c.coupon_id)}
											disabled={removeCoupon.isPending}
										>
											<X className="h-3.5 w-3.5" />
										</button>
									</li>
								))}
							</ul>
						) : (
							<p className="mb-4 text-sm text-ink-faint">No coupons applied.</p>
						)}
						<form
							className="flex flex-wrap gap-2"
							onSubmit={(e) => {
								e.preventDefault()
								if (couponCode.trim()) applyCoupon.mutate()
							}}
						>
							<input
								className="input max-w-xs"
								placeholder="Coupon code"
								value={couponCode}
								onChange={(e) => setCouponCode(e.target.value)}
							/>
							<button type="submit" className="btn-secondary" disabled={!couponCode.trim() || applyCoupon.isPending}>
								Apply coupon
							</button>
						</form>
					</section>
				)}

				<section className="card lg:col-span-3">
					<h2 className="border-b border-line p-4 text-sm font-semibold">Activity</h2>
					{!billable ? (
						<EmptyState message="No billing activity for a non-Chargebee instance." />
					) : timeline.isPending ? (
						<Spinner />
					) : timeline.error ? (
						<ErrorState error={timeline.error} />
					) : timeline.data.rows.length === 0 ? (
						<EmptyState message="Nothing recorded yet." />
					) : (
						<ul className="divide-y divide-line">
							{timeline.data.rows.map((row) => (
								<li key={row.id} className="flex items-start justify-between gap-3 p-3">
									<div className="min-w-0">
										<div className="truncate text-sm">{row.summary}</div>
										<div className="mono">
											{formatDate(row.createdAt)} · {row.actorEmail}
										</div>
									</div>
									{row.status === 'failure' ? <Badge tone="danger">failed</Badge> : <Badge tone="neutral">{row.actorType}</Badge>}
								</li>
							))}
						</ul>
					)}
				</section>
			</div>

			<ConfirmDialog
				open={dialog === 'cancel'}
				title="Cancel this subscription?"
				description={
					<>
						<label className="mt-1 flex items-start gap-2">
							<input type="checkbox" className="mt-1" checked={endOfTerm} onChange={(e) => setEndOfTerm(e.target.checked)} />
							<span>
								Cancel at end of term
								<span className="block text-xs text-ink-faint">
									Unchecked cancels immediately and forfeits paid-for time.
								</span>
							</span>
						</label>
						<input
							className="input mt-3"
							placeholder="Reason (recorded in the activity log)"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
						/>
						<p className="mt-3 text-xs">
							This is sent to Chargebee. The instance follows automatically once Chargebee reports the change.
						</p>
					</>
				}
				confirmLabel={endOfTerm ? 'Cancel at term end' : 'Cancel immediately'}
				tone="danger"
				busy={cancel.isPending}
				onConfirm={() => cancel.mutate()}
				onCancel={() => setDialog(null)}
			/>

			<ConfirmDialog
				open={dialog === 'reactivate'}
				title="Reactivate this subscription?"
				description="Billing resumes in Chargebee and the instance is restored on the next reconcile."
				confirmLabel="Reactivate"
				busy={reactivate.isPending}
				onConfirm={() => reactivate.mutate()}
				onCancel={() => setDialog(null)}
			/>
		</>
	)
}
